import React, { useEffect, useRef, useState } from 'react'
import * as PropTypes from 'prop-types'
import classNames from 'classnames'
import cls from './tabs.module.scss'

/**
 * Горизонтальные табы с controlled и uncontrolled режимами выбора.
 * @param {object} props
 * @returns {JSX.Element|null}
 */
export const Tabs = props => {
  const {
    className,
    classNameContainer,
    style,
    tabs = [],
    tabsIds,
    value,
    defaultSelected = 0,
    onTabChange,
    isPrint,
    fullWidth = true,
    additionalBlocks = [],
    idContainer,
    tabsMinWidth,
    maxWidth,
    showOverflowFade = true,
  } = props
  const [selectedTab, setSelectedTab] = useState(defaultSelected)
  const tabListRef = useRef(null)
  const tabRefs = useRef([])
  const isControlled = value !== undefined
  const activeTab = isControlled ? value : selectedTab

  useEffect(() => {
    const tabList = tabListRef.current
    const activeTabElement = tabRefs.current[activeTab]
    if (!tabList || !activeTabElement) return

    const tabListRect = tabList.getBoundingClientRect()
    const activeTabRect = activeTabElement.getBoundingClientRect()

    if (activeTabRect.left < tabListRect.left) {
      tabList.scrollLeft -= tabListRect.left - activeTabRect.left
    } else if (activeTabRect.right > tabListRect.right) {
      tabList.scrollLeft += activeTabRect.right - tabListRect.right
    }
  }, [activeTab])

  if (isPrint) return null

  const handleTabChange = index => {
    if (!isControlled) setSelectedTab(index)
    onTabChange?.(index)
  }

  const rootStyle = {
    ...(maxWidth !== undefined && {
      maxWidth,
      marginLeft: 'auto',
      marginRight: 'auto',
    }),
    ...style,
    ...(tabsMinWidth !== undefined && { '--tabs-min-width': `${tabsMinWidth}px` }),
  }

  return (
    <div className={classNames(cls.root, className)} style={rootStyle}>
      <div className={classNames(cls.container, classNameContainer)}>
        <div className={classNames(cls.tabsViewport, showOverflowFade && cls.withOverflowFade)}>
          <div
            ref={tabListRef}
            id={idContainer}
            className={classNames(cls.tabList, {
              [cls.fullWidth]: fullWidth,
              [cls.fitContent]: !fullWidth,
            })}
            role="tablist"
          >
            {tabs.map((tab, index) => {
              const label = tab?.label || tab || ''
              const pin = tab?.pin
              const selected = activeTab === index

              return (
                <button
                  key={index}
                  ref={element => {
                    tabRefs.current[index] = element
                  }}
                  id={(tabsIds && tabsIds[index]) || (idContainer && `${idContainer}_${index}_tab`)}
                  type="button"
                  className={classNames(cls.tab, selected && cls.selected)}
                  role="tab"
                  aria-selected={selected}
                  tabIndex={selected ? 0 : -1}
                  onClick={() => handleTabChange(index)}
                >
                  <span className={cls.label}>
                    {label}
                    {pin > 0 && <span className={cls.pin}>{pin}</span>}
                  </span>
                </button>
              )
            })}
          </div>
        </div>
        {additionalBlocks}
      </div>
    </div>
  )
}

Tabs.propTypes = {
  className: PropTypes.string,
  classNameContainer: PropTypes.string,
  style: PropTypes.object,
  tabs: PropTypes.array,
  tabsIds: PropTypes.array,
  value: PropTypes.number,
  defaultSelected: PropTypes.number,
  onTabChange: PropTypes.func,
  isPrint: PropTypes.bool,
  fullWidth: PropTypes.bool,
  additionalBlocks: PropTypes.node,
  idContainer: PropTypes.string,
  tabsMinWidth: PropTypes.number,
  maxWidth: PropTypes.number,
  showOverflowFade: PropTypes.bool,
}
